import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { recordingsApi } from '../../api/flashbackApi/recordings.ts';
import { HttpError } from '../../api/flashbackApi/http.ts';
import { formatLocalYmd, getViewerUtcOffsetMinutes } from '../../lib/date-ymd.ts';
import type { CameraTimeline, RecordingStreamKind } from '../../api/flashbackApi/types.ts';

const TODAY_POLL_MS = 30_000;

interface UseTimelineState {
  data: CameraTimeline | null;
  isLoading: boolean;
  error: string | null;
}

interface UseTimelineResult extends UseTimelineState {
  date: string;
  setDate: (date: string) => void;
  refresh: () => void;
}

interface UseTimelineOptions {
  initialDate?: string;
  /** `360` or `ptz` relay folder; omit for default (360 when configured). */
  stream?: RecordingStreamKind;
  /** When false, skips fetch (e.g. PTZ timeline when camera has no dual stream). */
  enabled?: boolean;
  /** When true, skips background poll / visibility refetch (e.g. while recording is playing). */
  pausePolling?: boolean;
}

export function useTimeline(cameraId: string, options: UseTimelineOptions = {}): UseTimelineResult {
  const [date, setDate] = useState(
    () => options.initialDate?.trim() || formatLocalYmd(new Date()),
  );
  const [state, setState] = useState<UseTimelineState>({
    data: null,
    isLoading: true,
    error: null,
  });
  const [reloadGeneration, setReloadGeneration] = useState(0);
  /** When true, the next fetch is a background poll (no full-screen loading state). */
  const quietNextFetchRef = useRef(false);

  const enabled = options.enabled !== false;
  const pausePolling = options.pausePolling === true;

  const query = useMemo(
    () => ({
      date,
      utcOffsetMinutes: getViewerUtcOffsetMinutes(),
      ...(options.stream ? { stream: options.stream } : {}),
    }),
    [date, options.stream],
  );

  const refresh = useCallback(() => {
    quietNextFetchRef.current = false;
    setState((s) => ({ ...s, isLoading: true }));
    setReloadGeneration((g) => g + 1);
  }, []);

  const refreshQuiet = useCallback(() => {
    quietNextFetchRef.current = true;
    setReloadGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    if (pausePolling || date !== formatLocalYmd(new Date())) return undefined;
    const id = window.setInterval(() => {
      refreshQuiet();
    }, TODAY_POLL_MS);
    return () => window.clearInterval(id);
  }, [date, pausePolling, refreshQuiet]);

  useEffect(() => {
    if (pausePolling) return undefined;
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (date === formatLocalYmd(new Date())) refreshQuiet();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [date, pausePolling, refreshQuiet]);

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, isLoading: false, error: null });
      return undefined;
    }

    const controller = new AbortController();
    const quiet = quietNextFetchRef.current;
    quietNextFetchRef.current = false;

    if (!quiet) {
      setState((s) => ({ ...s, isLoading: true, error: null }));
    }

    recordingsApi
      .getTimeline(cameraId, query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load playback timeline';
        setState((s) => ({ data: s.data, isLoading: false, error: message }));
      });

    return () => controller.abort();
  }, [cameraId, enabled, query, reloadGeneration]);

  return { ...state, date, setDate, refresh };
}
